import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import html2pdf from "html2pdf.js";
import logo from "../assets/logo_basic.png";

const ResumeBuilder = () => {
  const navigate = useNavigate();

  const [data, setData] = useState({
    name: "",
    title: "",
    email: "",
    phone: "",
    location: "",
    summary: "",
    experience: "",
    education: "",
    skills: "",
    photo: null,
    photoPreview: null
  });

  const [isEditMode, setIsEditMode] = useState(false);

  // ----------------------------------------------------
  // Load Saved Resume
  // ----------------------------------------------------
// ----------------------------------------------------
// Load Saved Resume
// ----------------------------------------------------
useEffect(() => {
  const loadData = async () => {
    const userId = localStorage.getItem("userId");
    if (!userId) return;

    const res = await fetch(`https://reignitespirit-api-5bjl2sdaqq-uc.a.run.app/api/resume/get?userId=${userId}`);

    // ✅ Prevent crash if backend returns empty body
    let resume = null;
    try {
      const text = await res.text();
      resume = text ? JSON.parse(text) : null;
    } catch (err) {
      console.warn("No JSON returned from backend");
      resume = null;
    }

    if (!resume) {
      console.log("No resume found → new resume mode");
      return; // ⬅ don't load anything
    }

    // Populate fields
    setData(prev => ({
      ...prev,
      name: resume.fullName || "",
      title: resume.jobTitle || "",
      email: resume.email || "",
      phone: resume.phone || "",
      location: resume.location || "",
      summary: resume.summary || "",
      experience: resume.experience || "",
      education: resume.education || "",
      skills: resume.skills || "",
    }));

    // Photo
    if (resume.photoBase64) {
      setData(prev => ({
        ...prev,
        photoPreview: resume.photoBase64,
        photoBase64: resume.photoBase64
      }));
    }

    setIsEditMode(true);
  };

  loadData();
}, []);
const handleDownloadFromBackend = () => {
   const userId = localStorage.getItem("userId");
   window.open(`https://reignitespirit-api-5bjl2sdaqq-uc.a.run.app/api/resume/download-pdf?userId=${userId}`, "_blank");
};

  // ----------------------------------------------------
  // Input Handler
  // ----------------------------------------------------
  const handleChange = (field) => (e) => {
    setData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  // ----------------------------------------------------
  // Profile Photo Handler
  // ----------------------------------------------------
  const handlePhoto = (e) => {
  const file = e.target.files[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onloadend = () => {
    setData(prev => ({
      ...prev,
      photoPreview: reader.result,   // used for UI
      photoBase64: reader.result     // stored in DB
    }));
  };
  reader.readAsDataURL(file);
};

  // ----------------------------------------------------
  // Save Resume
  // ----------------------------------------------------
  const saveResume = async () => {
    const userId = localStorage.getItem("userId");
    if (!userId) return alert("Not logged in!");

const payload = {
  userId,
  fullName: data.name,
  jobTitle: data.title,
  email: data.email,
  phone: data.phone,
  location: data.location,
  summary: data.summary,
  experience: data.experience,
  education: data.education,
  skills: data.skills,
  photoBase64: data.photoBase64 || null

    };

    const res = await fetch("https://reignitespirit-api-5bjl2sdaqq-uc.a.run.app/api/resume/save", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });

    if (res.ok) {
      alert("Resume saved!");
      navigate("/dashboard");
    }
  };

  // ----------------------------------------------------
  // DOWNLOAD PDF (Perfect A4 layout)
  // ----------------------------------------------------
const downloadPDF = () => {
  const resumeElement = document.getElementById("resume-pdf");

  if (!resumeElement) {
    alert("Resume preview not found!");
    return;
  }

  const opt = {
    margin: 0,
    filename: `${data.name.replace(/\s+/g, "_")}_resume.pdf`,
    image: { type: "jpeg", quality: 1 },
    html2canvas: {
      scale: 3,
      useCORS: true
    },
    jsPDF: {
      unit: "px",
      format: [1600, 1100], // FULL LANDSCAPE SIZE
      orientation: "landscape"
    }
  };

  html2pdf().set(opt).from(resumeElement).save();
};
  // ----------------------------------------------------
  // UI
  // ----------------------------------------------------
  return (
  <div className="min-h-screen bg-[#0f0f16] text-white flex flex-col">

    {/* Header */}
    <header className="w-full bg-[#1a1a28] px-8 py-4 flex justify-between items-center shadow-lg">
      <h1 className="text-xl font-bold">
        AI Resume Builder {isEditMode && "(Editing)"}
      </h1>

      <div className="flex gap-4">
        <button
          onClick={() => navigate("/dashboard")}
          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-xl"
        >
          ← Back to Dashboard
        </button>

        <button
          onClick={downloadPDF}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-xl"
        >
          ⬇️ Download PDF
        </button>
<button onClick={handleDownloadFromBackend}>
  Download Backend PDF
</button>

        <button
          onClick={saveResume}
          className="px-4 py-2 bg-pink-600 hover:bg-pink-700 rounded-xl"
        >
          {isEditMode ? "Update Resume" : "Save Resume"}
        </button>
      </div>
    </header>

    {/* Main Layout */}
    <main className="flex gap-6 p-6">

      {/* Left Form */}
      <section className="w-1/2 bg-[#1a1a28] p-6 rounded-2xl shadow-xl">

        <h2 className="text-2xl font-semibold mb-6">Resume Details</h2>

        {/* Profile Photo */}
        <label className="text-gray-300 text-sm">Profile Photo</label>
        <input
          type="file"
          className="mt-2 mb-4 text-sm"
          onChange={handlePhoto}
        />

        {data.photoPreview && (
          <img
            src={data.photoPreview}
            alt="profile"
            className="w-28 h-28 rounded-full object-cover mb-6"
          />
        )}

        {/* Input Fields */}
        {[
          ["name", "Full Name"],
          ["title", "Job Title"],
          ["email", "Email"],
          ["phone", "Phone Number"],
          ["location", "Location"],
        ].map(([field, label]) => (
          <div className="mb-5" key={field}>
            <label className="text-gray-300 text-sm">{label}</label>
            <input
              value={data[field]}
              onChange={handleChange(field)}
              className="w-full p-3 rounded-lg bg-[#12121a] border border-gray-700 mt-2"
            />
          </div>
        ))}

        {/* Textareas */}
        {[
          ["summary", "Summary"],
          ["experience", "Experience"],
          ["education", "Education"],
          ["skills", "Skills"],
        ].map(([field, label]) => (
          <div className="mb-5" key={field}>
            <label className="text-gray-300 text-sm">{label}</label>
            <textarea
              value={data[field]}
              onChange={handleChange(field)}
              className="w-full p-3 rounded-lg bg-[#12121a] border border-gray-700 mt-2"
            />
          </div>
        ))}

      </section>

      {/* RIGHT PREVIEW */}
      <section
        id="resume-pdf"
        className="w-1/2 bg-gradient-to-b from-[#151521] to-[#1c1c2c] p-10 
                   text-white rounded-2xl shadow-2xl border border-[#2e2e44]"
      >
        <div className="flex flex-col">

          {/* Profile Photo */}
          <div className="w-40 h-40 rounded-full bg-gray-700 border-4 border-purple-500 
                          flex items-center justify-center mx-0">
            {data.photoPreview ? (
              <img
                src={data.photoPreview}
                className="w-full h-full object-cover rounded-full"
                alt="profile"
              />
            ) : (
              <span className="text-gray-300">No Photo</span>
            )}
          </div>

          {/* NAME */}
          <h1 className="mt-6 text-4xl font-bold">{data.name}</h1>
          <p className="text-lg text-purple-300">{data.title}</p>

          {/* Contact */}
          <div className="mt-4 text-sm text-gray-300 space-y-1">
            <p>Email: {data.email}</p>
            <p>Phone: {data.phone}</p>
            <p>Location: {data.location}</p>
          </div>

          <hr className="w-full mt-6 border-gray-600" />

          {/* Summary */}
          <h2 className="text-xl text-purple-400 mt-6 font-semibold">
            Professional Summary
          </h2>
          <p className="text-gray-300 mt-2">{data.summary}</p>

          {/* Experience */}
          <h2 className="text-xl text-purple-400 mt-6 font-semibold">Experience</h2>
          <p className="text-gray-300 mt-2">{data.experience}</p>

          {/* Education */}
          <h2 className="text-xl text-purple-400 mt-6 font-semibold">Education</h2>
          <p className="text-gray-300 mt-2">{data.education}</p>

          {/* Skills */}
          <h2 className="text-xl text-purple-400 mt-6 font-semibold">Skills</h2>
          <p className="text-gray-300 mt-2">{data.skills}</p>

        </div>
      </section>

      {/* ----------------------------------------------- */}
      {/* HIDDEN FULL PAGE LANDSCAPE PDF LAYOUT (PERFECT) */}
      {/* ----------------------------------------------- */}
      <section
        id="resume-pdf-full"
        className="absolute left-[-9999px] top-0 w-[1400px] h-auto 
                   bg-gradient-to-b from-[#151521] to-[#1c1c2c] 
                   text-white p-16"
      >
        <div className="flex flex-col">

          {/* Profile Photo */}
          <div className="w-44 h-44 rounded-full bg-gray-700 border-4 border-purple-500 flex items-center justify-center">
            {data.photoPreview ? (
              <img
                src={data.photoPreview}
                className="w-full h-full object-cover rounded-full"
                alt="profile"
              />
            ) : (
              <span className="text-gray-300">No Photo</span>
            )}
          </div>

          {/* Name + Title */}
          <h1 className="mt-8 text-5xl font-bold">{data.name}</h1>
          <p className="text-2xl text-purple-300">{data.title}</p>

          {/* Contact */}
          <div className="mt-6 text-xl text-gray-300 leading-relaxed">
            <p>Email: {data.email}</p>
            <p>Phone: {data.phone}</p>
            <p>Location: {data.location}</p>
          </div>

          <hr className="w-full mt-10 border-gray-600" />

          {/* Summary */}
          <h2 className="text-3xl text-purple-400 mt-10 font-semibold">Professional Summary</h2>
          <p className="text-gray-300 text-xl mt-4">{data.summary}</p>

          {/* Experience */}
          <h2 className="text-3xl text-purple-400 mt-10 font-semibold">Experience</h2>
          <p className="text-gray-300 text-xl mt-4">{data.experience}</p>

          {/* Education */}
          <h2 className="text-3xl text-purple-400 mt-10 font-semibold">Education</h2>
          <p className="text-gray-300 text-xl mt-4">{data.education}</p>

          {/* Skills */}
          <h2 className="text-3xl text-purple-400 mt-10 font-semibold">Skills</h2>
          <p className="text-gray-300 text-xl mt-4">{data.skills}</p>
        </div>
      </section>

    </main>
  </div>
);

}
export default ResumeBuilder;
